import React, { useState, useMemo } from 'react';
import { Skeleton } from '@mui/material';
import {
  useGetCronJobHistoryQuery,
  useGetCronJobTypesQuery,
  useTriggerCronJobMutation
} from 'store/services/admin/adminApi';
import { CronJobExecution, CronJobType } from 'types/interfaces/cronjob';
import AdminLayout from '../AdminLayout';
import TopDeckedIngestSection from './TopDeckedIngestSection';
import { formatDuration } from 'utils/formatDuration';
import MagicButton from 'components/MagicButton/MagicButton';
import ConfirmJobTriggerModal from 'components/ConfirmJobTriggerModal/ConfirmJobTriggerModal';
import { AssetUrls } from 'config/assets';
import ChevronDownIcon from 'components/Icons/ChevronDownIcon';
import './AdminCronJobsPage.css';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'SUCCEEDED', label: 'Succeeded' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'IN_PROGRESS', label: 'In Progress' }
];

const AdminCronJobsPage = () => {
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [isManualTriggerOpen, setIsManualTriggerOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedJobType, setSelectedJobType] = useState<CronJobType | null>(null);
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);
  const pageSize = 50;

  // Fetch cron job history with adaptive polling
  // Start with 30s polling, will adjust based on data
  const { data, isLoading, error, refetch } = useGetCronJobHistoryQuery(
    { page, pageSize, typeFilter, statusFilter },
    {
      pollingInterval: 30000,
      skip: !document.hasFocus(),
    }
  );

  // Check if any jobs are in progress for adaptive polling and live updates badge
  const hasInProgressJobs = useMemo(() => {
    return data?.executions?.some((e: CronJobExecution) => e.status === 'IN_PROGRESS') || false;
  }, [data]);

  // Fetch available job types
  const { data: jobTypesData } = useGetCronJobTypesQuery();

  // Trigger job mutation
  const [triggerJob, { isLoading: isTriggeringJob }] = useTriggerCronJobMutation();

  // Get list of currently running job types
  const runningJobTypes = useMemo(() => {
    if (!data?.executions) return new Set<string>();
    return new Set(
      data.executions
        .filter((e: CronJobExecution) => e.status === 'IN_PROGRESS')
        .map((e: CronJobExecution) => e.jobType)
    );
  }, [data]);

  // Format date as MM/DD/YY HH:MM (UTC)
  const formatCronJobDate = (isoDateString: string): string => {
    const date = new Date(isoDateString);
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const year = String(date.getUTCFullYear()).slice(-2);
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    return `${month}/${day}/${year} ${hours}:${minutes}`;
  };

  // Format triggered by display
  const formatTriggeredBy = (triggeredBy: string): React.ReactElement => {
    if (triggeredBy === 'AUTOMATIC') {
      return <span className="triggered-automatic">Automatic</span>;
    }

    const match = triggeredBy.match(/^MANUAL:(\d+)$/);
    if (match) {
      return (
        <span className="triggered-manual">
          <img
            src={AssetUrls.icons.navigation.adminUsers}
            alt=""
            className="admin-icon"
          />
          Admin #{match[1]}
        </span>
      );
    }

    return <span>{triggeredBy}</span>;
  };

  // Format metadata key from camelCase to Title Case
  const formatMetadataKey = (key: string): string => {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase());
  };

  // Get error message based on error status
  const getErrorMessage = (error: any): string => {
    const status = error?.status || error?.originalStatus;

    if (status === 401) return "Please log in to view cron jobs";
    if (status === 403) return "You do not have permission to view this resource";
    if (status === 404) return "Cron job data is temporarily unavailable";
    if (status === 500) return "Server error. Our team has been notified. Please try again in a few minutes.";
    if (status === 'FETCH_ERROR') return "Network error. Please check your internet connection.";
    if (status === 'TIMEOUT_ERROR') return "Request timed out. Please try again.";

    return "Unable to load cron jobs. Please try again.";
  };

  // Determine if retry button should be shown
  const shouldShowRetry = (error: any): boolean => {
    const status = error?.status || error?.originalStatus;
    return status !== 401 && status !== 403;
  };

  // Handle pagination
  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (data && page < data.pagination.totalPages) {
      setPage(page + 1);
    }
  };

  // Handle type filter selection
  const handleTypeFilterChange = (value: string) => {
    setTypeFilter(value);
    setIsTypeDropdownOpen(false);
    setPage(1);
  };

  // Handle status filter selection
  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setIsStatusDropdownOpen(false);
    setPage(1);
  };

  // Get selected type label
  const getSelectedTypeLabel = () => {
    if (typeFilter === 'all') return 'All Types';
    const selected = jobTypesData?.jobTypes.find((jt: CronJobType) => jt.type === typeFilter);
    return selected ? selected.display : 'All Types';
  };

  // Get selected status label
  const getSelectedStatusLabel = () => {
    const selected = STATUS_OPTIONS.find((opt) => opt.value === statusFilter);
    return selected ? selected.label : 'All Status';
  };

  // Handle manual job trigger
  const handleJobTriggerClick = (jobType: CronJobType) => {
    setSelectedJobType(jobType);
    setIsConfirmModalOpen(true);
    setIsManualTriggerOpen(false);
  };

  // Confirm and trigger job
  const handleConfirmTrigger = async () => {
    if (!selectedJobType) return;

    try {
      await triggerJob({
        type: selectedJobType.type,
        body: { confirm: true }
      }).unwrap();

      setIsConfirmModalOpen(false);
      setSelectedJobType(null);

      // Show success message (you could add a toast notification here)
      alert(`${selectedJobType.display} started successfully!`);
    } catch (err) {
      console.error('Failed to trigger job:', err);
      alert('Failed to trigger job. Please try again.');
    }
  };

  // Handle row click to expand/collapse
  const handleRowClick = (executionId: number, execution: CronJobExecution) => {
    if (execution.errorMessage || (execution.metadata && Object.keys(execution.metadata).length > 0)) {
      setExpandedRowId(expandedRowId === executionId ? null : executionId);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <AdminLayout title="Admin Panel - Cron Jobs" currentPath="/admin/cronjobs">
        <div className="admin-cronjobs-page">
          <div className="admin-cronjobs-header">
            <h2 className="admin-cronjobs-title">Cron Job History</h2>
            <p className="admin-cronjobs-subtitle">
              Monitor automated background jobs and trigger manual executions
            </p>
          </div>

          <div className="admin-cronjobs-controls">
            <div className="admin-cronjobs-filter-dropdown">
              <span>All Types</span>
              <ChevronDownIcon className="dropdown-arrow" />
            </div>
            <div className="admin-cronjobs-filter-dropdown">
              <span>All Status</span>
              <ChevronDownIcon className="dropdown-arrow" />
            </div>
            <MagicButton
              label="Manual Job Trigger"
              color="purple"
              onPress={() => {}}
              isDisabled
            />
          </div>

          <div className="admin-cronjobs-content">
            <div className="admin-cronjobs-table">
              <div className="admin-cronjobs-table-header">
                <div>Date Started</div>
                <div>Date Completed</div>
                <div>Type</div>
                <div>Triggered By</div>
                <div>Status</div>
              </div>
              <div className="admin-cronjobs-loading">
                {[...Array(10)].map((_, index) => (
                  <div key={index} className="admin-cronjob-row-skeleton">
                    <Skeleton variant="text" width={120} height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                    <Skeleton variant="text" width={120} height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                    <Skeleton variant="text" width={140} height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                    <Skeleton variant="text" width={100} height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                    <Skeleton variant="text" width={100} height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <TopDeckedIngestSection />
        </div>
      </AdminLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <AdminLayout title="Admin Panel - Cron Jobs" currentPath="/admin/cronjobs">
        <div className="admin-cronjobs-page">
          <div className="admin-cronjobs-header">
            <h2 className="admin-cronjobs-title">Cron Job History</h2>
          </div>

          <div className="admin-cronjobs-content">
            <div className="admin-cronjobs-error-state">
              <div className="error-icon">⚠️</div>
              <h2 className="error-title">Unable to Load Cron Jobs</h2>
              <p className="error-message">{getErrorMessage(error)}</p>
              {shouldShowRetry(error) && (
                <MagicButton
                  label="Try Again"
                  color="purple"
                  onPress={() => refetch()}
                />
              )}
            </div>
          </div>
          <TopDeckedIngestSection />
        </div>
      </AdminLayout>
    );
  }

  // Empty state
  if (data && data.executions.length === 0) {
    return (
      <AdminLayout title="Admin Panel - Cron Jobs" currentPath="/admin/cronjobs">
        <div className="admin-cronjobs-page">
          <div className="admin-cronjobs-header">
            <h2 className="admin-cronjobs-title">Cron Job History</h2>
            <p className="admin-cronjobs-subtitle">
              Monitor automated background jobs and trigger manual executions
            </p>
          </div>

          <div className="admin-cronjobs-controls">
            <div
              className="admin-cronjobs-filter-dropdown"
              onClick={() => setIsTypeDropdownOpen(!isTypeDropdownOpen)}
            >
              <span>{getSelectedTypeLabel()}</span>
              <ChevronDownIcon className="dropdown-arrow" />
              {isTypeDropdownOpen && jobTypesData && (
                <div className="filter-dropdown-menu">
                  <div
                    className={`filter-dropdown-item ${typeFilter === 'all' ? 'selected' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTypeFilterChange('all');
                    }}
                  >
                    All Types
                  </div>
                  {jobTypesData.jobTypes.map((jobType: CronJobType) => (
                    <div
                      key={jobType.type}
                      className={`filter-dropdown-item ${typeFilter === jobType.type ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTypeFilterChange(jobType.type);
                      }}
                    >
                      {jobType.display}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div
              className="admin-cronjobs-filter-dropdown"
              onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
            >
              <span>{getSelectedStatusLabel()}</span>
              <ChevronDownIcon className="dropdown-arrow" />
              {isStatusDropdownOpen && (
                <div className="filter-dropdown-menu">
                  {STATUS_OPTIONS.map((option) => (
                    <div
                      key={option.value}
                      className={`filter-dropdown-item ${statusFilter === option.value ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStatusFilterChange(option.value);
                      }}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <MagicButton
              label="Manual Job Trigger"
              color="purple"
              onPress={() => setIsManualTriggerOpen(!isManualTriggerOpen)}
            />
          </div>

          <div className="admin-cronjobs-content">
            <div className="admin-cronjobs-empty-state">
              <div className="empty-state-icon">
                <img
                  src={`${AssetUrls.base}/assets/icons/clock.svg`}
                  alt="No jobs"
                  style={{ width: 64, height: 64, opacity: 0.3 }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
              <h2 className="empty-state-title">No Cron Jobs Found</h2>
              <p className="empty-state-description">
                {typeFilter !== 'all' || statusFilter !== 'all'
                  ? "No jobs match your filter criteria. Try adjusting your filters."
                  : "There are no cron jobs in the system yet."}
              </p>
            </div>
          </div>
          <TopDeckedIngestSection />
        </div>
      </AdminLayout>
    );
  }

  // Success state with data
  return (
    <AdminLayout title="Admin Panel - Cron Jobs" currentPath="/admin/cronjobs">
      <div className="admin-cronjobs-page">
        <div className="admin-cronjobs-header">
          <div>
            <h2 className="admin-cronjobs-title">Cron Job History</h2>
            <p className="admin-cronjobs-subtitle">
              Monitor automated background jobs and trigger manual executions
            </p>
          </div>
          {hasInProgressJobs && (
            <div className="live-updates-badge">
              <div className="pulse-dot" />
              Live Updates (5s)
            </div>
          )}
        </div>

        <div className="admin-cronjobs-controls">
          {/* Type Filter Dropdown */}
          <div
            className="admin-cronjobs-filter-dropdown"
            onClick={() => setIsTypeDropdownOpen(!isTypeDropdownOpen)}
          >
            <span>{getSelectedTypeLabel()}</span>
            <ChevronDownIcon className="dropdown-arrow" />
            {isTypeDropdownOpen && jobTypesData && (
              <div className="filter-dropdown-menu">
                <div
                  className={`filter-dropdown-item ${typeFilter === 'all' ? 'selected' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTypeFilterChange('all');
                  }}
                >
                  All Types
                </div>
                {jobTypesData.jobTypes.map((jobType: CronJobType) => (
                  <div
                    key={jobType.type}
                    className={`filter-dropdown-item ${typeFilter === jobType.type ? 'selected' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTypeFilterChange(jobType.type);
                    }}
                  >
                    {jobType.display}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Status Filter Dropdown */}
          <div
            className="admin-cronjobs-filter-dropdown"
            onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
          >
            <span>{getSelectedStatusLabel()}</span>
            <ChevronDownIcon className="dropdown-arrow" />
            {isStatusDropdownOpen && (
              <div className="filter-dropdown-menu">
                {STATUS_OPTIONS.map((option) => (
                  <div
                    key={option.value}
                    className={`filter-dropdown-item ${statusFilter === option.value ? 'selected' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStatusFilterChange(option.value);
                    }}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Manual Job Trigger Dropdown */}
          <div className="manual-trigger-container">
            <MagicButton
              label="Manual Job Trigger"
              color="purple"
              onPress={() => setIsManualTriggerOpen(!isManualTriggerOpen)}
            />
            {isManualTriggerOpen && jobTypesData && (
              <div className="trigger-dropdown-menu">
                  {jobTypesData.jobTypes.map((jobType: CronJobType) => {
                  const isRunning = runningJobTypes.has(jobType.type);

                  return (
                    <button
                      key={jobType.type}
                      className={`trigger-dropdown-item ${isRunning ? 'disabled' : ''}`}
                      onClick={() => {
                        if (!isRunning) {
                          handleJobTriggerClick(jobType);
                        }
                      }}
                      disabled={isRunning}
                    >
                      <div className="job-type-info">
                        <strong>{jobType.display}</strong>
                        <small className="job-description-small">{jobType.description}</small>
                        <small className="schedule-info">{jobType.schedule}</small>
                      </div>
                      {isRunning && (
                        <span className="running-badge">
                          <div className="pulse-dot" />
                          Running...
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="admin-cronjobs-content">
          <div className="admin-cronjobs-table">
            {/* Table Header */}
            <div className="admin-cronjobs-table-header">
              <div>Date Started</div>
              <div>Date Completed</div>
              <div>Type</div>
              <div>Triggered By</div>
              <div>Status</div>
            </div>

            {/* Table Body */}
            <div className="admin-cronjobs-table-body">
              {data?.executions.map((execution: CronJobExecution) => {
                const isExpandable = execution.errorMessage ||
                  (execution.metadata && Object.keys(execution.metadata).length > 0);
                const isExpanded = expandedRowId === execution.id;

                return (
                  <div key={execution.id}>
                    <div
                      className={`admin-cronjob-row ${isExpandable ? 'clickable' : ''} ${
                        execution.status === 'FAILED' ? 'failed-row' : ''
                      }`}
                      onClick={() => handleRowClick(execution.id, execution)}
                    >
                      {/* DATE STARTED */}
                      <div className="admin-cronjobs-col-date">
                        {formatCronJobDate(execution.startedAt)}
                      </div>

                      {/* DATE COMPLETED */}
                      <div className="admin-cronjobs-col-date">
                        {execution.completedAt ? (
                          formatCronJobDate(execution.completedAt)
                        ) : (
                          <span className="in-progress-text">In Progress...</span>
                        )}
                      </div>

                      {/* TYPE */}
                      <div className="admin-cronjobs-col-type">
                        {execution.jobTypeDisplay}
                      </div>

                      {/* TRIGGERED BY */}
                      <div className="admin-cronjobs-col-triggered">
                        {formatTriggeredBy(execution.triggeredBy)}
                      </div>

                      {/* STATUS */}
                      <div className="admin-cronjobs-col-status">
                        <span className={`status-badge status-${execution.status.toLowerCase()}`}>
                          {execution.statusDisplay}
                        </span>
                      </div>
                    </div>

                    {/* Expandable Details Row */}
                    {isExpanded && (
                      <div className="admin-cronjob-details-row">
                        {/* Error Message */}
                        {execution.status === 'FAILED' && execution.errorMessage && (
                          <div className="error-details">
                            <strong>Error Details:</strong>
                            <pre className="error-stack-trace">{execution.errorMessage}</pre>
                          </div>
                        )}

                        {/* Job Metadata */}
                        {execution.metadata && Object.keys(execution.metadata).length > 0 && (
                          <div className="job-metadata">
                            <div className="metadata-header">
                              <strong>Job Statistics:</strong>
                              {execution.durationMs && (
                                <span className="duration-badge">
                                  Duration: {formatDuration(execution.durationMs)}
                                </span>
                              )}
                            </div>
                            <div className="metadata-grid">
                              {Object.entries(execution.metadata).map(([key, value]) => (
                                <div key={key} className="metadata-item">
                                  <span className="metadata-label">
                                    {formatMetadataKey(key)}:
                                  </span>
                                  <span className="metadata-value">
                                    {typeof value === 'object' ? JSON.stringify(value) : value}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Pagination Controls */}
          {data && data.pagination.totalPages > 1 && (
            <div className="pagination-controls">
              <MagicButton
                label="Previous"
                color="grey"
                onPress={handlePreviousPage}
                isDisabled={page === 1}
              />

              <span className="pagination-info">
                Page {data.pagination.currentPage} of {data.pagination.totalPages}
                {' '}({data.pagination.totalExecutions} total jobs)
              </span>

              <MagicButton
                label="Next"
                color="grey"
                onPress={handleNextPage}
                isDisabled={page >= data.pagination.totalPages}
              />
            </div>
          )}
        </div>
        <TopDeckedIngestSection />
      </div>

      {/* Confirmation Modal */}
      <ConfirmJobTriggerModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setSelectedJobType(null);
        }}
        onConfirm={handleConfirmTrigger}
        jobType={selectedJobType}
        isLoading={isTriggeringJob}
      />
    </AdminLayout>
  );
};

export default AdminCronJobsPage;
